import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
    Alert,
    AlertColor,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Link,
    Snackbar,
    SxProps,
    TextField,
    Theme,
    Typography
} from '@mui/material'
import { usePathname } from 'next/navigation'
import React, { useState } from 'react'
import type { AppDispatch } from '../../../../redux/store'
import type { GoogleUserData } from '../../../../types/auth'
import { logError } from '../../../../utils/logError'
import GoogleSignInButton from '../../../common/GoogleSignInButton'

interface FormState {
    email: string
    password: string
    isEmailValid: boolean
    isPasswordValid: boolean
    rememberCheckedStatus: boolean
}

interface FormHandlers {
    onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    setRememberCheckedStatus: (checked: boolean) => void
    onForgotClick: () => void
    onSubmit: (e: React.FormEvent) => void
}

interface StatusState {
    loading: boolean
    error: string | null
    success: string | null
}

interface SignInFormProps {
    isMobile: boolean
    form: FormState
    status: StatusState
    handlers: FormHandlers
    t: (key: string) => string
    tHeader: (key: string) => string
    onGoogleSignIn?: (userData: GoogleUserData) => void
    dispatch?: AppDispatch
    sx?: SxProps<Theme>
}

const SignInForm: React.FC<SignInFormProps> = ({
    isMobile,
    form,
    status,
    handlers,
    t,
    tHeader,
    onGoogleSignIn,
    dispatch,
    sx = {}
}) => {
    const pathname = usePathname()
    const locale = pathname.split('/')[1] || 'en'
    const [showPassword, setShowPassword] = useState(false)
    const [notification, setNotification] = useState<{
        open: boolean
        message: string
        severity: AlertColor
    }>({ open: false, message: '', severity: 'info' })

    // Toggle password visibility
    const handleClickShowPassword = () => setShowPassword(show => !show)
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
    }

    // Handle Google Sign-In success
    const handleGoogleSignInSuccess = (userData: GoogleUserData) => {
        onGoogleSignIn?.(userData)
    }

    // Handle Google Sign-In error
    const handleGoogleSignInError = (error: Error) => {
        logError('SignInForm:GoogleSignIn', error)
        showNotification(error.message || t('googleSignInError'), 'error')
    }

    // Show notification
    const showNotification = (message: string, severity: AlertColor = 'error') => {
        setNotification({ open: true, message, severity })
    }

    // Handle notification close
    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }))
    }

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        handlers.onSubmit(e)
    }

    return (
        <Box sx={sx}>
            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>

            <form onSubmit={handleSubmit}>
                <GoogleSignInButton
                    onSuccess={handleGoogleSignInSuccess}
                    onError={handleGoogleSignInError}
                    buttonText="Google"
                    fullWidth
                    sx={{ mb: isMobile ? 1.5 : 3 }}
                />

                <Typography align="center" sx={{ color: 'grey.500', fontWeight: 500 }}>
                    {t('or')}
                </Typography>

                <TextField
                    fullWidth
                    label={t('email')}
                    type="email"
                    variant="outlined"
                    margin="normal"
                    autoComplete="email"
                    value={form.email}
                    onChange={handlers.onEmailChange}
                    error={!form.isEmailValid}
                    helperText={!form.isEmailValid ? t('invalidEmail') : ''}
                    disabled={status.loading}
                />

                <TextField
                    fullWidth
                    label={t('password')}
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    margin="normal"
                    autoComplete="current-password"
                    value={form.password}
                    onChange={handlers.onPasswordChange}
                    error={!form.isPasswordValid}
                    helperText={!form.isPasswordValid ? t('invalidPassword') : ''}
                    disabled={status.loading}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                    disabled={status.loading}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        my: 2
                    }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.rememberCheckedStatus}
                                onChange={e => handlers.setRememberCheckedStatus(e.target.checked)}
                                disabled={status.loading}
                            />
                        }
                        label={
                            <Typography variant="body2" fontSize={isMobile ? 12 : 14} color="text.secondary">
                                {t('keepMeSignedIn')}
                            </Typography>
                        }
                    />
                    <Button
                        onClick={handlers.onForgotClick}
                        disabled={status.loading}
                        sx={{
                            textTransform: 'none',
                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                            minWidth: 'auto',
                            p: 0
                        }}
                    >
                        {t('forgotPassword')}?
                    </Button>
                </Box>
                <Typography variant="caption" sx={{ color: 'grey.600', mb: 2, display: 'block', lineHeight: 1.7 }}>
                    {t('bySigningIn')}{' '}
                    <Link
                        href={`/termsOfUse.${locale}.html`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            fontSize: 12,
                            mx: 0.5,
                            color: 'primary.main',
                            fontWeight: 500,
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {t('termsOfUse')}
                    </Link>{' '}
                    {tHeader('and')}{' '}
                    <Link
                        href={`/privacyPolicy.${locale}.html`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            fontSize: 12,
                            mx: 0.5,
                            color: 'primary.main',
                            fontWeight: 500,
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {t('privacyPolicy')}
                    </Link>
                </Typography>
                <Button
                    type="submit"
                    variant="contained"
                    sx={{ width: '100%', mt: 2, fontWeight: 600, fontSize: 16 }}
                    disabled={status.loading}
                >
                    {status.loading ? <CircularProgress size={24} color="inherit" /> : t('signIn')}
                </Button>
            </form>
        </Box>
    )
}

export default SignInForm
